package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.islamicmedia.shared.presentation.AuthViewModel
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Post-signup 6-digit email OTP modal. Rendered once at the page root and shown
 * whenever [AuthViewModel] flips `awaitingVerification` true — so it covers ALL
 * web signup paths (SignupPrompt, inline registration, etc.), not just the
 * dedicated AuthScreen.
 *
 * Mirrors the mobile EmailVerificationDialog and the (currently unused) web
 * AuthScreen modal so the experience is identical across platforms.
 */
@Composable
fun EmailVerificationModal(viewModel: AuthViewModel) {
    val uiState by viewModel.uiState.collectAsState()
    if (!uiState.awaitingVerification) return

    Div(attrs = {
        style {
            property("position", "fixed")
            property("inset", "0")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("background", "rgba(0,0,0,0.5)")
            property("z-index", "2000")
            padding(24.px)
        }
    }) {
        Div(attrs = {
            style {
                backgroundColor(Color.white)
                borderRadius(20.px)
                property("box-shadow", CardShadow)
                padding(28.px)
                width(100.percent)
                property("max-width", "380px")
                property("box-sizing", "border-box")
            }
        }) {
            H2(attrs = { style { fontSize(22.px); fontWeight(700); color(Primary); property("margin", "0 0 8px") } }) {
                Text("Almost there!")
            }
            P(attrs = { style { fontSize(14.px); color(Gray700); property("margin", "0 0 18px") } }) {
                val email = uiState.verificationEmail
                Text(
                    if (!email.isNullOrBlank())
                        "To complete your registration, enter the 6-digit code we emailed to $email."
                    else
                        "To complete your registration, enter the 6-digit code we emailed you to verify your account."
                )
            }
            TextInput(value = uiState.verificationCode) {
                onInput { viewModel.onVerificationCodeChanged(it.value) }
                attr("inputmode", "numeric")
                attr("maxlength", "6")
                attr("placeholder", "123456")
                onKeyDown { if (it.key == "Enter") viewModel.verifyCode() }
                style {
                    width(100.percent); padding(12.px, 16.px); fontSize(18.px)
                    property("letter-spacing", "6px"); property("text-align", "center")
                    borderRadius(10.px); property("border", "1px solid $Gray200")
                    property("outline", "none"); property("box-sizing", "border-box")
                }
            }
            uiState.error?.let {
                P(attrs = { style { color(ScoreRed); fontSize(13.px); property("margin", "8px 0 0") } }) { Text(it) }
            }
            uiState.verificationNotice?.let {
                P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "8px 0 0") } }) { Text(it) }
            }
            val canVerify = uiState.verificationCode.length == 6 && !uiState.isSubmitting
            Div(attrs = {
                style {
                    marginTop(18.px); width(100.percent); padding(12.px)
                    borderRadius(10.px)
                    backgroundColor(if (canVerify) Primary else Gray300)
                    color(Color.white); fontSize(15.px); fontWeight(600)
                    property("text-align", "center"); property("box-sizing", "border-box")
                    cursor(if (canVerify) "pointer" else "default")
                }
                if (canVerify) onClick { viewModel.verifyCode() }
            }) { Text(if (uiState.isSubmitting) "Verifying…" else "Verify") }
            Div(attrs = {
                style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); marginTop(14.px) }
            }) {
                Span(attrs = {
                    style { color(Primary); fontSize(13.px); cursor("pointer") }
                    onClick { viewModel.resendCode() }
                }) { Text("Resend code") }
                Span(attrs = {
                    style { color(Gray500); fontSize(13.px); cursor("pointer") }
                    onClick { viewModel.cancelVerification() }
                }) { Text("Back") }
            }
        }
    }
}
